import { useCallback, useEffect, useState, Fragment } from 'react';
import { useRouter } from 'next/router';
import { useDispatch, useSelector } from 'react-redux';

import FreeLesson from '@cont/FreeLesson/FreeLesson';
import { getIsFetchingUserData, getIsLoggedIn } from '@selectors/auth';
import {
  fetchCourseData,
  fetchFreeLessonListFromLessonUID,
  fetchLessonData,
  fetchFreeRelatedCourses
} from '@api/fetchers/freelesson';
import SEO from '@seo/index';
import lessonSchema from '@seo/lesson/lesson';
import Error from '@comp/Error/Error';
import { readCookie } from '@utils/cookies';
import { UnAuthorizedError } from '@utils/APICustomErrors';
import PlusHeaderSkeleton from '@skeletons/Course/PlusHeader';
import Layout from '@layout/index';
import { useToggleable } from '@stores/toggleables';
import { LOGIN_DRAWER } from '@constants/toggleables';
import escape from '@utils/escapeHTML';
import PageReduxProvider from '@cont/App/PageReduxProviderWrapper';
import { getAuthorFullName } from '@api/processors/search';

const FreeLessonPage = ({ freeLessonPageData }) => {
  const isLoggedIn = useSelector(getIsLoggedIn);
  const fetchingUserData = useSelector(getIsFetchingUserData);
  const dispatch = useDispatch();

  const [freeLessonPageState, setFreeLessonPageState] =
    useState(freeLessonPageData);
  const router = useRouter();
  const {
    freeLessonInfo,
    courseInfo,
    authorInfo,
    lessonUID,
    freeLessonList,
    freeRelatedCourses
  } = freeLessonPageState || {};

  const {
    coverPhoto: coursecoverPhoto,
    thumbnail: courseThumbnail,
    ogImage,
    goal = {}
  } = courseInfo || {};

  useEffect(() => {
    setFreeLessonPageState(freeLessonPageData);
  }, [freeLessonPageData]);

  const { freeLessonInfo: freeLessonInfoFromServer } = freeLessonPageData || {};

  useEffect(() => {
    if (
      freeLessonInfoFromServer?.slug &&
      freeLessonInfoFromServer?.uid &&
      router.query.lessonSlug !== freeLessonInfoFromServer.slug
    )
      // Slug Correction
      router.replace(
        '/lesson/[lessonSlug]/[lessonUID]',
        `/lesson/${freeLessonInfoFromServer?.slug}/${freeLessonInfoFromServer.uid}/`,
        { shallow: true }
      );
  }, [
    router,
    freeLessonInfoFromServer?.uid,
    freeLessonInfoFromServer?.slug,
    freeLessonInfoFromServer?.collection?.relativeLink
  ]);

  const fetchLessonRelatedInfoForUser = useCallback(async () => {
    const accessToken = readCookie('accessToken');
    const [userSpecificLessonData, userSpecificLessonList] = await Promise.all([
      fetchLessonData(lessonUID, accessToken),
      fetchFreeLessonListFromLessonUID(lessonUID, accessToken)
    ]);
    if (!userSpecificLessonData.errorCode && userSpecificLessonData.uid)
      setFreeLessonPageState((lessonPageState) => ({
        ...lessonPageState,
        freeLessonInfo: userSpecificLessonData,
        freeLessonList: userSpecificLessonList?.results || []
      }));
  }, [lessonUID]);

  useEffect(() => {
    // Refetch lesson data once for 'watched' state
    if (isLoggedIn) fetchLessonRelatedInfoForUser();
  }, [isLoggedIn, fetchLessonRelatedInfoForUser]);

  const { openElement: openLoginDrawer } = useToggleable(LOGIN_DRAWER);

  useEffect(() => {
    if (
      !freeLessonPageData?.lessonUID &&
      !readCookie('accessToken') &&
      !fetchingUserData
    ) {
      if (!readCookie('accessToken') && !fetchingUserData) openLoginDrawer();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, dispatch, fetchingUserData, freeLessonPageData?.lessonUID]);

  if (freeLessonPageData?.isPrivateLesson) return <PlusHeaderSkeleton />;
  if (!courseInfo) return <Error />;

  const courseName =
    freeLessonInfo?.name || freeLessonInfo?.title || 'Free Lesson';
  const goalName = goal?.name || '';
  const metaTitle = `${goalName} - ${courseName} Offered by Unacademy`;
  const metaDescription = `Get access to the latest ${courseName} prepared with ${goalName} course curated
  by ${getAuthorFullName(
    authorInfo || {}
  )} on Unacademy to prepare for the toughest competitive exam.`;
  const { thumbnail, uid, slug } = freeLessonInfo || {};
  const canonicalURL = `https://unacademy.com/lesson/${slug}/${uid}`;
  return (
    <Fragment key={uid}>
      <SEO
        title={metaTitle}
        url={canonicalURL}
        description={metaDescription}
        openGraph={{
          title: metaTitle,
          description: metaDescription,
          images: [
            {
              url: ogImage || thumbnail || coursecoverPhoto || courseThumbnail
            }
          ],
          url: canonicalURL
        }}
        schema={lessonSchema({
          freeLessonList,
          freeLessonInfo,
          courseInfo,
          description: metaDescription
        })}
      />
      <FreeLesson
        freeLessonList={freeLessonList}
        freeLessonInfo={freeLessonInfo}
        courseInfo={courseInfo}
        lessonUID={lessonUID}
        freeRelatedCourses={freeRelatedCourses}
      />
    </Fragment>
  );
};

FreeLessonPage.pageInfo = {
  name: 'Old Course Page',
  layoutOptions: {
    hideIntercom: true,
    hideFreshbot: true
  },
  isMWebSupported: true
};

export default FreeLessonPage;

export const getStaticProps = async ({ params: query }) => {
  try {
    const { lessonUID } = query;
    const promises = [
      fetchFreeLessonListFromLessonUID(lessonUID),
      fetchLessonData(lessonUID)
    ];
    const [lessonList, lessonInfo] = await Promise.all(promises);

    if (!lessonInfo || lessonInfo?.errorCode)
      return { notFound: true, revalidate: 60 };

    const { collection = {} } = lessonInfo;

    // Check if lesson is actually a paid class.
    let isPaid = false;
    if (
      (collection?.forPlus || collection?.forSubscription) &&
      !lessonInfo.video &&
      collection?.relativeLink
    )
      isPaid = true;

    if (isPaid)
      return {
        redirect: {
          destination: collection?.relativeLink || '/',
          permanent: true
        }
      };

    const courseUID = collection?.uid || null;
    const apiCalls = [fetchCourseData(courseUID)];

    if (collection?.uid)
      apiCalls.push(
        fetchFreeRelatedCourses({
          lessonUID,
          authorUID: lessonInfo?.author?.uid,
          topicUID: collection.uid,
          goalUID: collection.goalUid
        })
      );

    const [courseInfo, freeRelatedCourses] = await Promise.all(apiCalls);
    if (courseInfo?.errorCode) return { notFound: true, revalidate: 60 };

    return {
      props: {
        freeLessonPageData: {
          freeLessonInfo: lessonInfo,
          freeLessonList: lessonList?.results || [],
          authorInfo: lessonInfo?.author,
          courseInfo,
          lessonUID: escape(lessonUID),
          freeRelatedCourses:
            freeRelatedCourses?.results?.map((result) => {
              const processedData = result;
              processedData.key = escape(lessonUID);
              processedData.data = processedData?.data?.map?.((data) => ({
                ...(data || {}),
                ...(data.programme || {})
              }));
              return processedData;
            }) || []
        }
      }
    };
  } catch (err) {
    if (err instanceof UnAuthorizedError) {
      return {
        props: {
          freeLessonPageData: {
            isPrivateLesson: true
          }
        },
        revalidate: 5 * 60
      };
    }

    return { notFound: true, revalidate: 60 };
  }
};

export const getStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking'
  };
};
FreeLessonPage.Layout = Layout;
FreeLessonPage.Wrapper = PageReduxProvider;
